import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useQuery, useInfiniteQuery } from "react-query";
import { getHourlySales } from "../../services/analyticsService";
import { getGeneralInfo } from "../../services/generalInfoService";
import { getCommonProducts } from "../../services/commonProductService";
import { formatAmount } from "../../utils/formatters";
import { toast } from "react-toastify";

const toYMD = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const HourlySales = () => {
  useEffect(() => {
    const portalId = "datepicker-portal";
    let el = document.getElementById(portalId);
    if (!el) {
      el = document.createElement("div");
      el.id = portalId;
      el.style.position = "relative";
      el.style.zIndex = "9999";
      document.body.appendChild(el);
    }
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const STORAGE_KEY = "filters:hourly-sales";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) {}
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }

  const [selectedDate, setSelectedDate] = useState(() => {
    if (savedFilters?.selectedDate) return new Date(savedFilters.selectedDate);
    return new Date(today);
  });
  const [postingAccount, setPostingAccount] = useState(
    savedFilters?.postingAccount || ""
  );
  const [zone, setZone] = useState(savedFilters?.zone || "");
  const [state, setState] = useState(savedFilters?.state || "");
  const [selectedProductSku, setSelectedProductSku] = useState(savedFilters?.selectedProductSku || "");
  const [selectedProductName, setSelectedProductName] = useState(savedFilters?.selectedProductName || "");
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const productDropdownRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const prevZoneRef = useRef(zone);

  useEffect(() => {
    if (prevZoneRef.current !== zone) {
      setState("");
      prevZoneRef.current = zone;
    }
  }, [zone]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          selectedDate: selectedDate?.toISOString?.() || selectedDate,
          postingAccount,
          zone,
          state,
          selectedProductSku,
          selectedProductName,
        })
      );
    } catch (_) {}
  }, [selectedDate, postingAccount, zone, state, selectedProductSku, selectedProductName, TODAY_KEY]);

  const { data: statesData } = useQuery(
    ["general-info", "states"],
    () => getGeneralInfo("states"),
    { enabled: !!zone, staleTime: 10 * 60 * 1000 }
  );
  const statesForZone = useMemo(() => {
    if (!zone || !Array.isArray(statesData)) return [];
    return statesData.filter(
      (item) => String(item?.zone || "").toLowerCase() === zone.toLowerCase()
    );
  }, [zone, statesData]);

  // Product options: same as Sales Comparison (getCommonProducts with search, infinite scroll)
  const searchQueryForApi = productSearchQuery.trim();
  const PAGE_SIZE = 10;
  const {
    data: productOptionsInfiniteData,
    isLoading: productOptionsLoading,
    isFetchingNextPage,
    hasNextPage: productOptionsHasNextPage,
    fetchNextPage: fetchNextProductPage,
  } = useInfiniteQuery(
    ["commonProducts-search", searchQueryForApi],
    ({ pageParam = 1 }) =>
      getCommonProducts({
        page: pageParam,
        limit: PAGE_SIZE,
        sortField: "name",
        sortValue: "ASC",
        searchKey: searchQueryForApi,
      }),
    {
      enabled: productSearchOpen,
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.data?.hasNext ? (allPages?.length ?? 0) + 1 : undefined,
      staleTime: 60 * 1000,
    }
  );
  const productOptionsFromApi = useMemo(() => {
    const pages = productOptionsInfiniteData?.pages ?? [];
    return pages.flatMap((page) => {
      const list = Array.isArray(page?.data?.data) ? page.data.data : [];
      return list
        .filter((p) => p.baseSku)
        .map((p) => ({ product: p.name || "", sku: p.baseSku || "" }));
    });
  }, [productOptionsInfiniteData?.pages]);
  const productOptions = useMemo(
    () => [{ product: "All Products", sku: "" }, ...productOptionsFromApi],
    [productOptionsFromApi]
  );
  const selectedProductLabel = useMemo(() => {
    if (!selectedProductSku) return "All Products";
    return selectedProductName || selectedProductSku;
  }, [selectedProductSku, selectedProductName]);

  const startDateStr = `${toYMD(selectedDate)}T00:00`;
  const endDateStr = `${toYMD(selectedDate)}T23:59`;

  const {
    data: statsData,
    isLoading,
    error,
  } = useQuery(
    ["hourlySales", startDateStr, endDateStr, postingAccount, zone, state, selectedProductSku],
    () =>
      getHourlySales({   
        startMonth: startDateStr,
        endMonth: endDateStr,
        ...(postingAccount && { postingAccount }),
        ...(zone && { zone }),
        ...(state && { state: state.trim().toLowerCase() }),
        ...(selectedProductSku?.trim() && { baseSku: selectedProductSku.trim() }),
      }),
    {
      onError: (err) => {
        toast.error(err.message || "Failed to load hourly sales data");
      },
    }
  );

  const chartData = React.useMemo(() => {
    if (!statsData?.data?.length) return [];
    const grandTotalRow = statsData.data.find(
      (item) => item.stat_value === "Grand Total"
    );
    const hourlyData = grandTotalRow?.hourly_data;
    if (!hourlyData) return [];
    const keys = Object.keys(hourlyData).sort();
    return keys.map((key) => {
      const part = key.split("T");
      const hourPart = part.length > 1 ? part[1] : "00";
      const hourNum = parseInt(hourPart, 10);
      const nextHour = String((hourNum + 1) % 24).padStart(2, "0");
      const hourLabel = `${hourPart}:00 - ${nextHour}:00`;
      const item = hourlyData[key];
      return {
        hour: hourLabel,
        hourShort: hourPart + ":00",
        fullKey: key,
        net_amount: item?.net_amount ?? 0,
        qty: item?.qty ?? 0,
      };
    });
  }, [statsData]);

  const totalAmount = chartData.reduce((s, d) => s + d.net_amount, 0);
  const totalQty = chartData.reduce((s, d) => s + d.qty, 0);

  const handleExportCSV = () => {
    if (chartData.length === 0) {
      toast.info("No data to export");
      return;
    }
    setIsExporting(true);
    try {
      const headers = ["Time", "Net Sales (₹)", "Sales Count"];
      const rows = chartData.map((row) => [
        row.hour,
        formatAmount(row.net_amount),
        String(row.qty),
      ]);
      const totalRow = ["Total", formatAmount(totalAmount), String(totalQty)];
      const csvContent = [
        headers.join(","),
        ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")),
        totalRow.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const productPart = selectedProductSku
        ? `-${(selectedProductName || selectedProductSku).replace(/[/\\?%*:|"]/g, "-")}`
        : "";
      a.download = `hourly-sales-${toYMD(selectedDate)}${zone ? `-${zone}` : ""}${state ? `-${state}` : ""}${productPart}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const resetFilters = () => {
    const todayDate = new Date(today);
    setSelectedDate(todayDate);
    setPostingAccount("");
    setZone("");
    setState("");
    setSelectedProductSku("");
    setSelectedProductName("");
    setProductSearchOpen(false);
    setProductSearchQuery("");
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="dd MMM yyyy"
              className="border border-gray-300 rounded px-3 py-2 w-44"
              portalId="datepicker-portal"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform
            </label>
            <select
              value={postingAccount}
              onChange={(e) => setPostingAccount(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-44"
            >
              <option value="">All</option>
              <option value="amazon">Amazon</option>
              <option value="flipkart">Flipkart</option>
              <option value="shopify">Shopify</option>
              <option value="tata 1mg">Tata 1mg</option>
              <option value="palsons derma pvt ltd">Palsons Derma Pvt Ltd</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zone
            </label>
            <select
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-44"
            >
              <option value="">All</option>
              <option value="north">North</option>
              <option value="south">South</option>
              <option value="east">East</option>
              <option value="west">West</option>
              <option value="central">Central</option>
              <option value="north east">North East</option>
            </select>
          </div>
          {zone && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-44"
              >
                <option value="">All</option>
                {statesForZone.map((item) => (
                  <option key={item.state} value={item.state}>
                    {item.state}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className={`relative ${productSearchOpen ? "z-[10001]" : ""}`}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product
            </label>
            <div ref={productDropdownRef} className="relative w-64">
              <input
                type="text"
                value={productSearchOpen ? productSearchQuery : selectedProductLabel}
                onChange={(e) => {
                  setProductSearchQuery(e.target.value);
                  setProductSearchOpen(true);
                }}
                onFocus={() => {
                  setProductSearchOpen(true);
                  setProductSearchQuery("");
                }}
                onClick={() => {
                  setProductSearchOpen(true);
                  setProductSearchQuery("");
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (productDropdownRef.current && !productDropdownRef.current.contains(document.activeElement)) {
                      setProductSearchOpen(false);
                    }
                  }, 200);
                }}
                placeholder="Search product or SKU..."
                className="border border-gray-300 rounded px-3 py-2 w-full pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
                {productSearchOpen ? "⌃" : "⌄"}
              </span>
              {productSearchOpen && (
                <ul
                  className="absolute z-[10001] mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg py-1 text-sm"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    if (!productOptionsHasNextPage || isFetchingNextPage) return;
                    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                    if (nearBottom) fetchNextProductPage();
                  }}
                >
                  {productOptionsLoading && productOptionsFromApi.length === 0 ? (
                    <li className="px-3 py-2 text-gray-500">{productSearchQuery.trim() ? "Searching..." : "Loading..."}</li>
                  ) : productOptions.length <= 1 ? (
                    <li className="px-3 py-2 text-gray-500">{productSearchQuery.trim() ? "No matches" : "Type to search or scroll for more"}</li>
                  ) : (
                    <>
                      {productOptions.map((opt) => (
                        <li
                          key={opt.sku ? `sku-${opt.sku}` : "all"}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex flex-col"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedProductSku(opt.sku || "");
                            setSelectedProductName(opt.product || "");
                            setProductSearchQuery("");
                            setProductSearchOpen(false);
                          }}
                        >
                          <span className="font-medium truncate" title={opt.product}>
                            {opt.product || "All Products"}
                          </span>
                          {opt.sku && (
                            <span className="text-xs text-gray-500 truncate">SKU: {opt.sku}</span>
                          )}
                        </li>
                      ))}
                      {productOptionsHasNextPage && (
                        <li className="px-3 py-2 text-gray-500 text-center border-t border-gray-100">
                          {isFetchingNextPage ? "Loading more..." : "Scroll for more"}
                        </li>
                      )}
                    </>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#049ED5]"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Reset filters
            </button>
          </div>
        </div>
      </div>

      {/* Chart View */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Hourly Sales Report
          </h3>
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={isExporting || chartData.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#049ED5] hover:bg-[#0380a8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#049ED5] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Exporting...
              </>
            ) : (
              "Export CSV"
            )}
          </button>
        </div>
        <div className="flex flex-wrap gap-6 mb-4">
          <div>
            <p className="text-sm text-gray-500">Total net sales (day)</p>
            <p className="text-xl font-semibold text-gray-900">
              {formatAmount(totalAmount)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total sales count</p>
            <p className="text-xl font-semibold text-gray-900">{totalQty}</p>
          </div>
        </div>

        {error && (
          <p className="text-red-600 py-4">
            {error.message || "Failed to load data"}
          </p>
        )}
        {isLoading && (
          <div className="h-80 flex items-center justify-center text-gray-500">
            Loading...
          </div>
        )}
        {!isLoading && !error && chartData.length === 0 && (
          <div className="h-80 flex items-center justify-center text-gray-500">
            No hourly data for this date.
          </div>
        )}
        {!isLoading && !error && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={380}>
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 50, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="hour"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{ fontSize: 12 }}
                label={{ value: "Sales Count", angle: -90, position: "insideLeft" }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`}
                label={{ value: "Net Sales", angle: 90, position: "insideRight" }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "Net Sales") return [formatAmount(value), name];
                  return [value, name];
                }}
                labelFormatter={(label) => `Time: ${label}`}
              />
              <Legend />
              <Bar
                yAxisId="right"
                dataKey="net_amount"
                fill="#049ED5"
                radius={[4, 4, 0, 0]}
                name="Net Sales"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="qty"
                stroke="#1f2937"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Sales Count"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Table View */}
      {!isLoading && !error && chartData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Hourly Sales Report for Report Time Range
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Sales
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales Count
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chartData.map((row) => (
                  <tr key={row.fullKey} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {row.hour}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                      {formatAmount(row.net_amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                      {row.qty}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    Total
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatAmount(totalAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                    {totalQty}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HourlySales;
